import { queryKeys } from '@packages/api';
import type { ClientProject } from '@packages/types';
import { listClientProjects } from '@packages/api/endpoints/client-project.js';
import { normaliseIds, resolvedByIdsQueryOptions, useResolvedByIds } from './useResolvedByIds';

export interface ResolvedClientProjects {
  /** The matter rows the account could resolve, or null while none have been read (in flight, failed, or nothing to ask for). */
  projects: readonly ClientProject[] | null;
  /** Matter id to row, derived from `projects`. */
  byId: Map<string, ClientProject>;
  /** True while the read is in flight, and false when there is nothing to read. */
  isPending: boolean;
  isError: boolean;
}

const CLIENT_PROJECT_SOURCE = {
  queryKey: queryKeys.clientProjects,
  list: (accountId: string, idsChunk: string[]) =>
    listClientProjects(accountId, { ids: idsChunk, limit: idsChunk.length, includeArchived: true }),
};

/**
 * Resolves the matter ids a surface holds (an invoice's `clientProjectId`) to
 * matter rows in one batched read, mirroring `useResolvedClients`. Null and
 * undefined ids are skipped, so a caller passes `invoice.clientProjectId`
 * straight through.
 *
 * `includeArchived: true`: an invoice keeps its matter after the matter is
 * archived, so leaving archived ones out would render a live invoice as if its
 * matter did not exist. Callers read `archivedAt` off the returned rows.
 */
export function useResolvedClientProjects(
  accountId: string | null | undefined,
  ids: readonly (string | null | undefined)[],
): ResolvedClientProjects {
  const { rows, byId, isPending, isError } = useResolvedByIds<ClientProject>(accountId, ids, CLIENT_PROJECT_SOURCE);
  return { projects: rows, byId, isPending, isError };
}

/** The read `useResolvedClientProjects` makes, for warming its cache ahead of a render. */
export function resolvedClientProjectsQueryOptions(accountId: string, ids: readonly (string | null | undefined)[]) {
  return resolvedByIdsQueryOptions<ClientProject>(accountId, normaliseIds(ids), CLIENT_PROJECT_SOURCE);
}
